import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { OtpStep } from './OtpStep';
import { PasswordStep } from './PasswordStep';
import { BrandLogo } from '@/shared/ui/BrandLogo';
import { useAuth } from '@/app/providers/AuthProvider';
import {
  buildScopedPath,
  isLikelyGuid,
  parseScopeFromPath,
  publicRoutes,
} from '@/app/routes';
import type { LoginResponseLike } from '@/shared/auth/storage';

type Tab = 'sms' | 'password';

const HERO_POINTS = [
  { icon: 'shop-window', text: 'ПОС касс, И-Баримт, ээлжийн хаалт нэг дор' },
  { icon: 'box-seam', text: 'Агуулах, тооллого, өртгийн тооцоо автоматаар' },
  { icon: 'graph-up-arrow', text: 'Борлуулалт, авлага, өглөгийн тайлан бодит цагт' },
];

export function LoginPage() {
  const [tab, setTab] = useState<Tab>('sms');
  const { loginFromResponse } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from ?? null;

  function handle(res: LoginResponseLike) {
    loginFromResponse(res);
    // Хэрэглэгчийн default tenant + company-аас scoped URL барина.
    const tid = res.DefaultTenantId ?? null;
    const cid = res.DefaultCompanyId ?? null;
    if (isLikelyGuid(tid) && isLikelyGuid(cid)) {
      // Хэрэв original URL нь scoped байсан бол түүн рүү буцаах нь зөв
      // (deep-link return). Зөвхөн `from` нь login биш, scoped URL бол.
      const fromScope = from ? parseScopeFromPath(from) : null;
      if (from && fromScope && fromScope.tenantId === tid && fromScope.companyId === cid) {
        nav(from, { replace: true });
        return;
      }
      nav(buildScopedPath('dashboard', { tenantId: tid, companyId: cid }), {
        replace: true,
      });
      return;
    }
    // Default scope байхгүй бол сонгуулна.
    nav(publicRoutes.selectScope, { replace: true });
  }

  return (
    <div className="auth-page">
      <aside className="auth-hero" aria-hidden="true">
        <Link to={publicRoutes.landing} className="auth-hero-brand">
          <BrandLogo variant="icon" size={40} />
          <span style={{ color: '#fff' }}>
            Tse<span>Tsu</span>
          </span>
        </Link>
        <div className="auth-hero-body">
          <h2 className="auth-hero-title">Бизнесээ нэг дэлгэцээс удирд.</h2>
          <p className="auth-hero-sub">
            Жижиг, дунд бизнест зориулсан үүлэн систем. Касс, агуулах, санхүү бүгд
            монгол хэл дээр, ямар ч төхөөрөмжөөс.
          </p>
          <ul className="auth-hero-list">
            {HERO_POINTS.map((p) => (
              <li key={p.icon}>
                <i className={`bi bi-${p.icon}`} />
                {p.text}
              </li>
            ))}
          </ul>
        </div>
        <div className="auth-hero-foot">© {new Date().getFullYear()} TseTsu · Улаанбаатар</div>
      </aside>

      <main className="auth-panel">
        <Link to={publicRoutes.landing} className="auth-mobile-brand">
          <BrandLogo variant="icon" size={34} />
          <span>
            Tse<span>Tsu</span>
          </span>
        </Link>
        <div className="auth-card fade-in">
          <div className="auth-card-head">
            <h1 className="auth-card-title">Нэвтрэх</h1>
            <p className="auth-card-sub">Утасны дугаараараа системд нэвтэрнэ үү.</p>
          </div>

          <div className="auth-tabs" role="tablist" aria-label="Нэвтрэх арга">
            <button
              type="button"
              role="tab"
              className="auth-tab"
              aria-selected={tab === 'sms'}
              onClick={() => setTab('sms')}
            >
              <i className="bi bi-chat-dots" /> SMS-ээр
            </button>
            <button
              type="button"
              role="tab"
              className="auth-tab"
              aria-selected={tab === 'password'}
              onClick={() => setTab('password')}
            >
              <i className="bi bi-key" /> Нууц үгээр
            </button>
          </div>

          {tab === 'sms' ? <OtpStep onLoggedIn={handle} /> : <PasswordStep onLoggedIn={handle} />}
        </div>
        <Link to={publicRoutes.landing} className="auth-back">
          <i className="bi bi-arrow-left" /> Нүүр хуудас руу буцах
        </Link>
      </main>
    </div>
  );
}
